import {
  Box,
  Flex,
  Image,
  Link,
  Text,
  VisuallyHidden,
  chakra,
  useColorModeValue,
  Stack
} from "@chakra-ui/react";
import React from "react";
import { FaFreeCodeCamp, FaLinkedin } from "react-icons/fa";
import { RiInstagramFill } from "react-icons/ri";
import { IoLogoWhatsapp } from "react-icons/io";
import { SiHashnode } from "react-icons/si";
import { FaDev } from "react-icons/fa";
import { IoMdDownload } from "react-icons/io";
import HeroImage from "../../src/assets/hero_abstract.png";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

export const Home = () => {
  const primaryColor = "#F0451A";

  const SocialButton = ({ children, label, href }) => {
    return (
      <chakra.button
        bg={useColorModeValue("blackAlpha.100", "whiteAlpha.100")}
        rounded={"full"}
        w={10}
        h={10}
        cursor={"pointer"}
        as={"a"}
        href={href}
        display={"inline-flex"}
        alignItems={"center"}
        justifyContent={"center"}
        transition={"background 0.3s ease, transform 0.2s ease"}
        _hover={{
          bg: useColorModeValue("blackAlpha.200", "whiteAlpha.200"),
          transform: "translateY(-3px)",
          color: primaryColor
        }}
        target="_blank"
        rel="noopener noreferrer"
      >
        <VisuallyHidden>{label}</VisuallyHidden>
        {children}
      </chakra.button>
    );
  };

  return (
    <Box
      minH="100vh"
      position={"relative"}
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      pt={{ base: 20, md: 0 }}
    >
        {/* Background blobs for premium feel */}
        <Box
          position="absolute"
          top="-10%"
          left="-10%"
          w="500px"
          h="500px"
          bg="orange.200"
          filter="blur(100px)"
          opacity="0.4"
          borderRadius="full"
          zIndex={-1}
        />
        <Box
          position="absolute"
          bottom="-10%"
          right="-10%"
          w="400px"
          h="400px"
          bg="purple.200"
          filter="blur(100px)"
          opacity="0.4"
          borderRadius="full"
          zIndex={-1}
        />

      <Flex
        direction={{ base: "column", md: "row" }}
        alignItems="center"
        justifyContent="space-between"
        w="full"
        maxW="7xl"
        px={{ base: 6, md: 12, lg: 20 }}
        gap={{ base: 10, md: 20 }}
      >
        
        {/* Social Sidebar - Desktop */}
        <Flex
          direction="column"
          gap="6"
          display={{ base: "none", md: "flex" }}
          position="fixed"
          left="10"
          top="50%"
          transform="translateY(-50%)"
          zIndex="10"
        >
          <SocialButton label={"WhatsApp"} href={"https://wa.me/qr/AT3JFAE4XNAAL1"}>
            <IoLogoWhatsapp size={"20"} />
          </SocialButton>
          <SocialButton label={"Instagram"} href={"https://www.instagram.com/dev_ayodeji?igsh=YXMwbjJsaHcxbHJk"}>
            <RiInstagramFill size={"20"} />
          </SocialButton>
          <SocialButton label={"linkedIn"} href={"https://www.linkedin.com/in/dennis-temoye-7325551b4?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app"}>
            <FaLinkedin size={"20"} />
          </SocialButton>
          <SocialButton label={"FreeCodeCamp"} href={"https://www.freecodecamp.org/news/author/dennis/"}>
            <FaFreeCodeCamp size={"20"} />
          </SocialButton>
          <SocialButton label={"Hashnode"} href={"https://dennisblog.hashnode.dev/"}>
            <SiHashnode size={"20"} />
          </SocialButton>
          <SocialButton label={"Dev.to"} href={"https://dev.to/dennis1001"}>
            <FaDev size={"20"} />
          </SocialButton>
          <Box w="1px" h="100px" bg="gray.400" mt="4" mx="auto" />
        </Flex>

        {/* Text Content */}
        <MotionFlex
          direction="column"
          alignItems={{ base: "center", md: "flex-start" }}
          textAlign={{ base: "center", md: "left" }}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          flex={1}
        >
          <Text fontSize={"lg"} fontWeight="medium" mb={2}>
            Hello! I'm
          </Text>
          <Text 
            fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }} 
            fontWeight="bold" 
            color={primaryColor}
            lineHeight="1.1"
            mb={4}
          >
            Dennis Temoye
          </Text>
          <Text 
            fontSize={{ base: "2xl", md: "4xl" }} 
            fontWeight="bold" 
            color="gray.700"
            lineHeight="1.2"
          >
            Frontend Developer <br /> & Technical Writer
          </Text>
          
          <Text
            mt="6"
            fontSize={{ base: "md", md: "lg" }}
            color="gray.600"
            maxW="xl"
            lineHeight="tall"
          >
            I am genuinely passionate about building beautiful, functional web applications and sharing my knowledge through writing. I thrive on tackling challenges that push my skills to new heights.
          </Text>

          <Stack direction={{ base: "column", sm: "row" }} spacing={4} mt="8">
            <Link
                as={motion.a}
                href={require("../../src/assets/Resume/DennisTemoyeResume .pdf")}
                download="Temoye Dennis.pdf"
                display="flex"
                alignItems="center"
                justifyContent="center"
                px="8"
                py="3"
                fontSize={"md"}
                fontWeight="bold"
                borderRadius="full"
                bg={primaryColor}
                color="white"
                _hover={{ textDecoration: 'none', bg: "red.600" }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                boxShadow="lg"
            >
              Download CV <Box as={IoMdDownload} ml={2} />
            </Link>
             {/* Mobile Socials */}
             <Flex display={{ base: "flex", md: "none" }} gap="4" mt="4" justify="center">
                <SocialButton label={"linkedIn"} href={"https://www.linkedin.com/in/dennis-temoye-7325551b4?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app"}>
                    <FaLinkedin size={"20"} />
                </SocialButton>
                <SocialButton label={"Dev.to"} href={"https://dev.to/dennis1001"}>
                    <FaDev size={"20"} />
                </SocialButton>
             </Flex>
          </Stack>
        </MotionFlex>

        {/* Hero Image */}
        <MotionBox
            flex={1}
            display={{ base: "none", md: "flex" }}
            justifyContent="center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
        >
             <Box position="relative">
                <Box 
                    position="absolute"
                    inset="0"
                    bgGradient={`linear(to-br, ${primaryColor}, transparent)`}
                    borderRadius="full"
                    filter="blur(40px)"
                    opacity={0.3}
                    zIndex={-1}
                    // Abstract hero image
                />
                <Image 
                    src={HeroImage} 
                    alt="Abstract Tech Visualization" 
                    maxW="500px" 
                    objectFit="contain" 
                    filter="drop-shadow(0px 10px 20px rgba(0,0,0,0.15))"
                />
             </Box>
        </MotionBox>
      </Flex>
    </Box>
  );
};
